import React, { useState, useMemo } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import debounce from "lodash.debounce";
import "./ModernDataTable.scss";

/**
 * Modern Reusable DataTable Component with Server-Side Search & Pagination
 * Supports RTL layout
 *
 * @param {Array} data - Array of data objects to display
 * @param {Array} columns - Array of column configurations
 * @param {boolean} loading - Loading state
 * @param {string} searchPlaceholder - Placeholder text for search input
 * @param {boolean} showSearch - Show/hide search input (default: true)
 * @param {boolean} showPagination - Show/hide pagination (default: true)
 * @param {number} rowsPerPage - Initial rows per page (default: 10)
 * @param {Array} rowsPerPageOptions - Options for rows per page dropdown
 * @param {boolean} expandable - Enable row expansion
 * @param {function} rowExpansionTemplate - Template for expanded row content
 * @param {function} onRowToggle - Callback when row is expanded/collapsed
 * @param {any} expandedRows - Currently expanded rows
 * @param {string} emptyMessage - Message when no data available
 * @param {string} className - Additional CSS classes
 *
 * Server-side props:
 * @param {number} totalRecords - Total count from API (count field)
 * @param {number} currentPage - Current page number (1-based)
 * @param {number|null} nextPage - Next page number from API (null if no next)
 * @param {number|null} prevPage - Previous page number from API (null if no prev)
 * @param {function} onPageChange - Callback when page changes: (page, pageSize) => void
 * @param {function} onSearch - Callback when search changes: (searchTerm) => void
 * @param {number} searchDebounce - Debounce delay for search in ms (default: 300)
 */
function ModernDataTable({
	data = [],
	columns = [],
	loading = false,
	searchPlaceholder = "بحث...",
	showSearch = true,
	showPagination = true,
	rowsPerPage = 10,
	rowsPerPageOptions = [5, 10, 25, 50],
	expandable = false,
	rowExpansionTemplate,
	onRowToggle,
	expandedRows,
	emptyMessage = "لا توجد بيانات",
	className = "",
	// Server-side pagination props
	totalRecords = 0,
	currentPage = 1,
	nextPage = null,
	prevPage = null,
	onPageChange,
	onSearch,
	searchDebounce = 300,
}) {
	const [searchValue, setSearchValue] = useState("");
	const [rows, setRows] = useState(rowsPerPage);

	// Calculate first index for DataTable
	const first = (currentPage - 1) * rows;

	// Debounced search function
	const debouncedSearch = useMemo(
		() =>
			debounce((value, searchFn) => {
				if (searchFn) {
					searchFn(value);
				}
			}, searchDebounce),
		[searchDebounce],
	);

	// Handle search input change
	const handleSearchChange = (e) => {
		const value = e.target.value;
		setSearchValue(value);
		debouncedSearch(value, onSearch);
	};

	// Handle clear search
	const handleClearSearch = () => {
		setSearchValue("");
		if (onSearch) {
			onSearch("");
		}
	};

	// Handle page change from DataTable
	const handlePageChange = (event) => {
		const newPage = Math.floor(event.first / event.rows) + 1;
		const newRows = event.rows;

		if (newRows !== rows) {
			setRows(newRows);
		}

		if (onPageChange) {
			onPageChange(newPage, newRows);
		}
	};

	const allowExpansion = (rowData) => {
		return expandable;
	};

	// Custom paginator template for RTL
	const paginatorTemplate = {
		layout: "RowsPerPageDropdown CurrentPageReport PrevPageLink NextPageLink",
		RowsPerPageDropdown: (options) => {
			return (
				<div className="d-flex align-items-center gap-2 ms-3">
					<span className="paginator-label">عدد الصفوف:</span>
					<Dropdown
						value={rows}
						options={rowsPerPageOptions.map((opt) => ({
							label: opt,
							value: opt,
						}))}
						onChange={(e) => {
							const newRows = e.value;
							setRows(newRows);
							if (onPageChange) {
								onPageChange(1, newRows); // Reset to first page when changing page size
							}
						}}
						className="rows-dropdown"
					/>
				</div>
			);
		},
		CurrentPageReport: () => {
			const start = totalRecords === 0 ? 0 : first + 1;
			const end = Math.min(first + rows, totalRecords);
			return (
				<span className="paginator-info mx-3">
					{start} - {end} من {totalRecords}
				</span>
			);
		},
		PrevPageLink: () => {
			const isDisabled = prevPage === null;
			return (
				<button
					type="button"
					className={`paginator-btn paginator-next ${isDisabled ? "disabled" : ""}`}
					onClick={() => {
						if (!isDisabled && onPageChange) {
							onPageChange(currentPage - 1, rows);
						}
					}}
					disabled={isDisabled}>
					<i className="pi pi-chevron-right"></i>
				</button>
			);
		},
		NextPageLink: () => {
			const isDisabled = nextPage === null;
			return (
				<button
					type="button"
					className={`paginator-btn paginator-prev ${isDisabled ? "disabled" : ""}`}
					onClick={() => {
						if (!isDisabled && onPageChange) {
							onPageChange(currentPage + 1, rows);
						}
					}}
					disabled={isDisabled}>
					<i className="pi pi-chevron-left"></i>
				</button>
			);
		},
	};

	const header = showSearch ? (
		<div className="modern-table-header">
			<div className="search-container">
				<span className="p-input-icon-right search-wrapper">
					<i className="pi pi-search search-icon" />
					<InputText
						value={searchValue}
						onChange={handleSearchChange}
						placeholder={searchPlaceholder}
						className="search-input"
					/>
					{searchValue && (
						<button
							type="button"
							className="clear-search-btn"
							onClick={handleClearSearch}>
							<i className="pi pi-times" />
						</button>
					)}
				</span>
			</div>
			<div className="results-count">
				<span>إجمالي النتائج: {totalRecords}</span>
			</div>
		</div>
	) : null;

	return (
		<div className={`modern-datatable-wrapper ${className}`} dir="rtl">
			<DataTable
				value={data}
				loading={loading}
				header={header}
				emptyMessage={emptyMessage}
				rowHover
				showGridlines
				scrollable
				className="modern-datatable"
				// Expansion props
				expandedRows={expandedRows}
				onRowToggle={onRowToggle}
				rowExpansionTemplate={rowExpansionTemplate}
				// Pagination - using lazy loading for server-side
				paginator={showPagination}
				lazy
				first={first}
				rows={rows}
				totalRecords={totalRecords}
				onPage={handlePageChange}
				paginatorTemplate={paginatorTemplate}
				rowsPerPageOptions={rowsPerPageOptions}>
				{expandable && (
					<Column expander={allowExpansion} style={{ width: "3rem" }} />
				)}
				{columns.map((col, index) => (
					<Column
						key={col.field || index}
						field={col.field}
						header={col.header}
						body={col.body}
						sortable={col.sortable}
						style={{ textAlign: "right", ...col.style }}
					/>
				))}
			</DataTable>
		</div>
	);
}

export default ModernDataTable;
